/**
 * §P.1 fase 2 · La administración de un alumno: sus papeles y su tarjeta.
 *
 * ## El token de una tarjeta se devuelve **una sola vez**
 *
 * En `student_cards` solo queda el hash: quien se lleve una copia de la tabla no se
 * lleva ninguna tarjeta que funcione, y **no hay forma de volver a enseñar el valor**.
 * Si se pierde, se emite otra.
 *
 * Y el contrato es explícito sobre de quién es el problema: «eso no es un olvido de
 * la interfaz, es la garantía: **decidlo en la pantalla antes** de que alguien cierre
 * el diálogo sin imprimir».
 *
 * ## Revocar no borra
 *
 * El fichaje del martes pasado tiene que seguir explicándose, y una tarjeta borrada
 * dejaría entradas de registro sin nada detrás.
 */

export type ClaseDeDocumentoDeAlumno =
  'seguro' | 'autorizacion' | 'licencia' | 'certificado_delitos' | 'otro';

export const CLASES_DE_DOCUMENTO_DE_ALUMNO: readonly {
  valor: ClaseDeDocumentoDeAlumno;
  etiqueta: string;
  explicacion: string;
}[] = [
  {
    valor: 'seguro',
    etiqueta: 'Seguro',
    explicacion: 'El deportivo o el de accidentes. Es el que más veces caduca.',
  },
  {
    valor: 'autorizacion',
    etiqueta: 'Autorización',
    explicacion:
      'Salidas, competiciones, permiso para volver solo a casa. Lo firma la familia.',
  },
  {
    valor: 'licencia',
    etiqueta: 'Licencia federativa',
    explicacion: 'Sin ella no se compite, y se renueva por temporada.',
  },
  {
    valor: 'certificado_delitos',
    etiqueta: 'Certificado de delitos sexuales',
    explicacion: 'Para un alumno mayor de edad que además ayuda en clase.',
  },
  { valor: 'otro', etiqueta: 'Otro', explicacion: 'Lo que no encaja arriba.' },
];

export const etiquetaDeDocumentoDeAlumno = (clase: string): string =>
  CLASES_DE_DOCUMENTO_DE_ALUMNO.find((c) => c.valor === clase)?.etiqueta ??
  clase;

export interface DocumentoDeAlumno {
  readonly id: string;
  readonly kind: string;
  readonly reference: string | null;
  readonly issued_on: Date | string | null;
  readonly expires_on: Date | string | null;
  /** `null` hasta que §S.3 permita subir el PDF desde el panel. */
  readonly asset_id: string | null;
  /** A treinta días o menos. **Lo calcula el servidor**, no la pantalla. */
  readonly expiring_soon: boolean;
  readonly expired: boolean;
  readonly verified_at: Date | string | null;
  readonly created_at: Date | string;
}

/**
 * El estado de un papel, en una palabra.
 *
 * Se leen **las banderas del servidor** y no se resta de fechas: `expiring_soon` ya
 * viene resuelto, y calcularlo aquí sería el segundo sitio que un día discrepa. Lo
 * único que decide la pantalla es que «sin fecha» no es «al día», por lo mismo que en
 * los papeles del personal: de un papel sin caducidad no sabemos nada.
 */
export type EstadoDePapel =
  'caducado' | 'caduca_pronto' | 'al_dia' | 'sin_fecha';

export function estadoDePapel(d: DocumentoDeAlumno): EstadoDePapel {
  if (d.expired) return 'caducado';
  if (d.expiring_soon) return 'caduca_pronto';
  return d.expires_on === null ? 'sin_fecha' : 'al_dia';
}

export const ESTADOS_DE_PAPEL: Record<
  EstadoDePapel,
  { etiqueta: string; clase: string }
> = {
  caducado: { etiqueta: 'Caducado', clase: 'pastilla-mal' },
  caduca_pronto: { etiqueta: 'Caduca pronto', clase: 'pastilla-paused' },
  al_dia: { etiqueta: 'Al día', clase: 'pastilla-bien' },
  sin_fecha: { etiqueta: 'Sin fecha de caducidad', clase: 'pastilla' },
};

export type ClaseDeTarjeta = 'qr' | 'nfc';

export const CLASES_DE_TARJETA: readonly {
  valor: ClaseDeTarjeta;
  etiqueta: string;
  explicacion: string;
}[] = [
  {
    valor: 'qr',
    etiqueta: 'Código QR',
    explicacion:
      'Se imprime y se pega en el carnet, o se guarda en el móvil de la familia.',
  },
  {
    valor: 'nfc',
    etiqueta: 'Llavero o tarjeta NFC',
    explicacion:
      'Se acerca al lector de la entrada. Hay que grabarla con el token.',
  },
];

export interface TarjetaDeAlumno {
  readonly id: string;
  readonly kind: string;
  readonly label: string | null;
  readonly issued_on: Date | string;
  readonly revoked_at: Date | string | null;
  readonly active: boolean;
}

/** Lo que devuelve emitir: la tarjeta y **el token, una sola vez**. */
export interface TarjetaEmitida {
  readonly card: TarjetaDeAlumno;
  readonly token: string;
}

export const etiquetaDeClaseDeTarjeta = (clase: string): string =>
  CLASES_DE_TARJETA.find((c) => c.valor === clase)?.etiqueta ?? clase;

/**
 * §P.1 fase 3 · Las tallas de un alumno.
 *
 * **`student_sizes` es un histórico y el `PUT` añade, no pisa.** La pantalla manda las
 * tallas de hoy enteras —de ahí el verbo— pero cada envío deja una fila nueva con su
 * fecha. Pisar la anterior mataría la sugerencia de septiembre —«el año pasado llevó
 * una 140»— por ahorrar seis filas al año, y esa sugerencia es media razón de ser de
 * §P.11.
 */
export interface TallaDeAlumno {
  readonly id: string;
  readonly category: string;
  readonly size: string;
  readonly recorded_on: string | Date;
  /** `manual` la apuntó el club; `request` salió de un pedido de material. */
  readonly source: string;
}

/**
 * Las categorías que se preguntan, y en el orden en que se miden a un niño.
 *
 * Es una lista sugerida, no un catálogo cerrado: la columna es texto libre y un club
 * puede tener apuntado «peto» de antes. Lo que llegue y no esté aquí se enseña igual
 * en el histórico — esconderlo sería perderlo.
 */
export const CATEGORIAS_DE_TALLA = [
  { id: 'dobok', etiqueta: 'Dobok', ejemplo: '140' },
  { id: 'cinturon', etiqueta: 'Cinturón', ejemplo: '260 cm' },
  { id: 'proteccion', etiqueta: 'Protecciones', ejemplo: 'M' },
  { id: 'calzado', etiqueta: 'Calzado', ejemplo: '36' },
  { id: 'camiseta', etiqueta: 'Camiseta del club', ejemplo: '12 años' },
] as const;

const NOMBRES: Readonly<Record<string, string>> = Object.fromEntries(
  CATEGORIAS_DE_TALLA.map((c) => [c.id, c.etiqueta])
);

export const etiquetaDeTalla = (categoria: string): string =>
  NOMBRES[categoria] ?? categoria;

/**
 * La última talla apuntada de cada categoría.
 *
 * El servidor las manda por fecha descendente, así que la primera de cada categoría
 * es la vigente. Lo que se reparte aquí es **lo vigente frente al histórico**, que es
 * la distinción que hace útil la pantalla: arriba lo que se usa para pedir, abajo lo
 * que dice cómo ha crecido.
 */
export function ultimasTallas(
  tallas: readonly TallaDeAlumno[]
): Map<string, TallaDeAlumno> {
  const vigentes = new Map<string, TallaDeAlumno>();
  for (const t of tallas) {
    if (!vigentes.has(t.category)) vigentes.set(t.category, t);
  }
  return vigentes;
}
